import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { Footer, Question, SelectLang } from '@/components';
import '@ant-design/v5-patch-for-react-19';
import { DownOutlined, UpOutlined } from '@ant-design/icons';
import {
  history,
  Link,
  type RequestConfig,
  type RunTimeLayoutConfig,
} from '@umijs/max';
import { Dropdown, Menu, type MenuProps } from 'antd';
import { debounce } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import DownLoadApp from '@/pages/home/downLoadApp/index'; // App下载-自定义内容
import FunctionServicesContent from '@/pages/home/functionServices/index'; // 功能服务-自定义内容
import SmartHardwareOverlay from '@/pages/home/smartHardware/index'; // 智能硬件-自定义内容
import Solution from '@/pages/home/solution/index'; // 解决方案-自定义内容
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
}> {
  return {
    settings: defaultSettings as Partial<LayoutSettings>,
  };
}

// 导航菜单配置
const navItems = [
  {
    path: '/',
    label: '首页',
    routes: [
      {
        path: 'functionServices',
        component: false,
      },
    ],
  },
  {
    path: 'http://mylianyungangdp.wtbsq.cn/index',
    name: 'daPing',
    label: '大屏',
    a: true,
  },
  {
    path: '/functionServices',
    name: 'functionServices',
    label: '功能服务',
    icon: <DownOutlined />,
    isCustomOverlay: true,
  },
  {
    path: '/solution',
    name: 'solution',
    label: '解决方案',
    icon: <DownOutlined />,
    isCustomOverlay: true,
  },
  {
    path: '/smartHardware',
    name: 'smartHardware',
    label: '智能硬件',
    icon: <DownOutlined />,
    isCustomOverlay: true,
  },
  {
    path: '/mallEntrance',
    name: 'mallEntrance',
    label: '成为商家',
    a: true,
  },
];
// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  const [hoveredKey, setHoveredKey] = useState<string>('');
  // 滚动距离和导航栏背景色
  const [scrollY, setScrollY] = useState(0);
  // 存储当前屏幕宽度（初始化时获取一次）
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  // 存储header元素的引用，避免每次都查询DOM
  const headerRef = useRef<HTMLElement | null>(null);
  // 初始化时获取.ant-layout-header元素
  useEffect(() => {
    headerRef.current = document.querySelector('.ant-layout-header');
  }, []);
  // 监听屏幕宽度变化
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // 根据条件更新header样式
  useEffect(() => {
    if (!headerRef.current) return;
    // 定义两个条件
    const isSmallScreen = screenWidth < 768;
    const isScrolledEnough = scrollY >= 120;
    const bgColor =
      isSmallScreen && isScrolledEnough ? 'rgb(38, 129, 243)' : 'transparent';
    headerRef.current.style.setProperty('background', bgColor, 'important');
    headerRef.current.style.transition = 'background 0.3s ease';
  }, [screenWidth, scrollY]);
  // 监听滚动事件
  useEffect(() => {
    const handleScroll = debounce(() => {
      setScrollY(window.scrollY);
    }, 50);
    // 绑定事件
    window.addEventListener('scroll', handleScroll);
    // 组件卸载时移除事件监听
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  // 根据滚动距离判断是否需要变色（超过100px则变色）
  const headerBg = scrollY >= 120 ? 'rgb(38, 129, 243)' : 'transparent';
  // 菜单标题点击状态
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: string]: boolean }>(
    {},
  );
  // 菜单头部点击事件处理
  const handleMenuHeaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // 点击Logo区域（示例：跳转到首页）
    const isLogo = (e.target as HTMLElement).closest('.ant-pro-layout-logo');
    if (isLogo) {
      e.preventDefault();
      history.push('/');
      return;
    }
    //  点击头部空白区域（非菜单、非下拉）
    const isMenuArea = (e.target as HTMLElement).closest('.ant-menu');
    const isDropdownArea = (e.target as HTMLElement).closest('.ant-dropdown');
    const isAntListItem = (e.target as HTMLElement).closest('.ant-list-item');
    const isZnyj = (e.target as HTMLElement).closest('.smartHardware_box a');
    if (isAntListItem || isZnyj) {
      const openDropdownKey = Object.keys(dropdownOpen).find(
        (key) => dropdownOpen[key],
      );
      if (openDropdownKey) {
        setDropdownOpen((prev) => ({
          ...prev,
          [openDropdownKey]: false,
        }));
      }
    }
    if (!isMenuArea && !isDropdownArea) {
      console.log('点击了菜单头部空白区域');
    }
  };
  // 生成菜单配置（保持箭头切换和标题不可点击）
  const generateMenuItems = (): MenuProps['items'] => {
    return navItems.map((item) => {
      if (item.isCustomOverlay) {
        const overlay =
          item.name === 'functionServices' ? (
            <FunctionServicesContent key="function-services" />
          ) : item.name === 'solution' ? (
            <Solution key="solution" />
          ) : item.name === 'downLoadApp' ? (
            <DownLoadApp key="download-app" />
          ) : (
            <SmartHardwareOverlay key="smart-hardware-overlay" />
          );

        return {
          key: item.path,
          label: (
            <Dropdown
              overlay={overlay}
              placement="bottom"
              trigger={['hover']}
              open={dropdownOpen[item.name]}
              // open={true}
              onOpenChange={(open, info) => {
                if (info.source === 'trigger') {
                  // 只更新当前下拉菜单的状态
                  setDropdownOpen((prev) => ({
                    ...prev,
                    [item.name]: open,
                  }));
                }
              }}
              onMouseEnter={() => {
                setHoveredKey(item.path);
                // 鼠标进入时打开当前下拉菜单
                setDropdownOpen((prev) => ({
                  ...prev,
                  [item.name]: true,
                }));
              }}
              onMouseLeave={() => {
                setHoveredKey('');
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                }}
              >
                <span>{item.label}</span>
                {hoveredKey === item.path ? <UpOutlined /> : item.icon}
              </div>
            </Dropdown>
          ),
        };
      }

      if (item.icon) {
        return {
          key: item.path,
          label: (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onMouseEnter={() => setHoveredKey(item.path)}
              onMouseLeave={() => setHoveredKey('')}
            >
              <span>{item.label}</span>
              {hoveredKey === item.path ? <UpOutlined /> : item.icon}
            </div>
          ),
          icon: null,
          children: item.children?.map((child: any) => ({
            key: child.path,
            label: <Link to={child.path}>{child.label}</Link>, // 子菜单可点击跳转
          })),
        };
      }
      return {
        key: item.path,
        label: item.a ? (
          <a href={item.path} target="_blank" rel="noopener noreferrer">
            {item.label}
          </a>
        ) : (
          <Link to={item.path}>{item.label}</Link>
        ),
      };
    });
  };
  return {
    onMenuHeaderClick: handleMenuHeaderClick,
    actionsRender: () => [
      <Question key="doc" />,
      <SelectLang key="SelectLang" />,
    ],
    footerRender: () => <Footer />,
    menuHeaderRender: (logo, title, props) => {
      const currentPath = history.location.pathname;
      return (
        <div
          className="home_header"
          style={{
            background: headerBg,
          }}
        >
          <div className="home_header_content">
            <div className="home_header_content_title">
              <div className="nav-logo">{logo}</div>
              <div className="nav-title">{props ? title : '屋托帮官网'}</div>
            </div>
            <Menu
              mode="horizontal"
              selectedKeys={[currentPath]}
              items={generateMenuItems()}
              theme="light"
              className="home_header_content_menu"
            />
          </div>
        </div>
      );
    },
    // 自定义 403 页面
    // unAccessible: <div>unAccessible</div>,
    // 增加一个 loading 的状态
    childrenRender: (children) => {
      // if (initialState?.loading) return <PageLoading />;
      return <>{children}</>;
    },
    ...initialState?.settings,
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 和 ahooks 的 useRequest 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 */
export const request: RequestConfig = {
  // baseURL: isDev ? "" : "https://proapi.azurewebsites.net",
  ...errorConfig,
};
